import * as CL from '@design-system/component-library';
import { Link, generatePath } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables.js';
import {
  dueDateMsg,
  invoicesMsg,
  modifiedMsg,
  ordersMsg,
  paidMsg,
  pendingApprovalMsg,
  redeemRequestsMsg,
  supportCasesAndMessagesMsg,
  t,
  waitingForActionMsg,
} from '../../common/i18n';
import { getCompanyName } from '../../common/utils/accountUtils';
import { formatDefinedTimestampToDDMMYYYY, formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils';
import { formatBalance, formatSum } from '../../common/utils/priceUtils';
import { truncateText } from '../../common/utils/stringUtils.js';
import { featureTranslation, statusData as supportCaseStatusData } from '../../common/utils/supportCaseUtils';
import { CustomerOrderStatus } from '../../generated/api/customerOrderStatus';
import { SupportCase } from '../../generated/api/supportCase';
import { getCustomerOrderStatusInfo } from '../CustomerOrderDetails/CustomerOrderDetailsUtil';
import { getStatusColumnInvoiceState } from '../InvoiceDetails/InvoiceDetails';
import { Loading } from '../Loading';
import { Status } from '../Status/Status';
import { statusData as redeemRequestStatusData } from '../SubscriptionAction/RedeemRequestList';
import type { CustomerOrderHeader } from '../../generated/api/customerOrderHeader';
import type { InvoiceHeader } from '../../generated/api/invoiceHeader';
import type { SubscriptionActionHeader } from '../../generated/api/subscriptionActionHeader';
import type { SupportCaseDataBundle } from '../../generated/api/supportCaseDataBundle';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { JSX } from 'react';

import './LinkListSummary.scss';

export enum LinkListSummaryType {
  INVOICES = 'invoices',
  REDEEM_REQUESTS = 'redeemRequests',
  ORDERS = 'orders',
  SUPPORT_CASES = 'supportCases',
}

export interface LinkListSummaryProps {
  linkListSummaryType: LinkListSummaryType;
  data: ListData;
  authenticatedUser?: AuthenticatedUser;
  loading?: boolean;
}

type ListData =
  | InvoiceHeader[]
  | SubscriptionActionHeader[]
  | CustomerOrderHeader[]
  | SupportCaseDataBundle[]
  | undefined;

interface LinkItemData {
  id: string;
  title: JSX.Element | string;
  subtitle?: JSX.Element | string;
  status?: JSX.Element | string;
  path: string;
}

const truncateTextMaxChars = 100;

const mapDataToLinkItems = (items: ListData, type: string, authenticatedUser?: AuthenticatedUser): LinkItemData[] => {
  switch (type) {
    case 'invoices':
      return (items as InvoiceHeader[]).map(item => ({
        id: item.invoiceDisplayId,
        title: (
          <>
            <h4>{item.invoiceDisplayId}</h4>
            <span className="of-link-list-summary__link__separator">|</span>
            <span className="of-link-list-summary__link__title__subtext">{item.payerName}</span>
          </>
        ),
        subtitle: `${t.P4RQ('Amount')} ${formatSum(item.sum)}, ${t.RJ27(paidMsg)} ${formatBalance(item.sum, item.balance || 0)}`,
        status: (
          <>
            <Status
              textAfterBadge={getStatusColumnInvoiceState(item.balance || 0, item.due).text}
              color={getStatusColumnInvoiceState(item.balance || 0, item.due).color}
            />
            <span className="of-link-list-summary__link__separator">|</span>
            <div className="of-link-list-summary__link__status__date">
              {t.LA93(dueDateMsg)} {formatTimestampToDDMMYYYY(item.due)}
            </div>
          </>
        ),
        path: `${generatePath(paths.INVOICE, { invoiceId: item.invoiceDisplayId })}?mdmId=${item.accountMasterId}`,
      }));

    case 'redeemRequests':
      return (items as SubscriptionActionHeader[]).map(item => ({
        id: item.subscriptionActionDisplayId,
        title: (
          <>
            <h4>{item.subscriptionActionDisplayId}</h4>
            <span className="of-link-list-summary__link__separator">|</span>
            <span className="of-link-list-summary__link__title__subtext">
              {getCompanyName(authenticatedUser, item.accountMasterId)}
            </span>
          </>
        ),
        subtitle: item.subscriptionContactName,
        status: (
          <>
            <Status
              textAfterBadge={redeemRequestStatusData(item.status).text}
              color={redeemRequestStatusData(item.status).color}
            />
            <span className="of-link-list-summary__link__separator">|</span>
            <div className="of-link-list-summary__link__status__date">
              {formatDefinedTimestampToDDMMYYYY(item.created)}
            </div>
          </>
        ),
        path: `${generatePath(paths.CUSTOMER_ORDER_REDEEM_REQUEST, { requestId: item.subscriptionActionDisplayId })}?mdmId=${item.accountMasterId}`,
      }));

    case 'orders':
      return (items as CustomerOrderHeader[]).map(item => ({
        id: item.customerOrderDisplayId || 'unknown',
        title: (
          <>
            <h4>{item.customerOrderDisplayId}</h4>
            <span className="of-link-list-summary__link__separator">|</span>
            <span className="of-link-list-summary__link__title__subtext">
              {getCompanyName(authenticatedUser, item.accountMasterId)}
            </span>
          </>
        ),
        subtitle: truncateText(
          (item.deliveryOrderContactNames?.length ?? 0) > 0
            ? item.deliveryOrderContactNames
            : item.deliveryOrderPurposeOfUses,
          truncateTextMaxChars
        ),
        status: (
          <>
            <Status
              textAfterBadge={getCustomerOrderStatusInfo(item.status).text}
              color={getCustomerOrderStatusInfo(item.status).color}
            />
            <span className="of-link-list-summary__link__separator">|</span>
            <div className="of-link-list-summary__link__status__date">
              {t.RTJW(modifiedMsg)} {formatTimestampToDDMMYYYY(item.created)}
            </div>
          </>
        ),
        path: `${generatePath(paths.CUSTOMER_ORDER, { orderId: item.customerOrderDisplayId })}?mdmId=${item.accountMasterId}`,
      }));

    case 'supportCases':
      return (items as SupportCaseDataBundle[]).map(item => ({
        id: item.supportCaseDisplayId,
        title: (
          <>
            <h4>{item.supportCaseDisplayId}</h4>
            <span className="of-link-list-summary__link__separator">|</span>
            <span className="of-link-list-summary__link__title__subtext">
              {getCompanyName(authenticatedUser, item.accountMasterId)}
            </span>
          </>
        ),
        subtitle: featureTranslation(item.feature),
        status: (
          <>
            <Status
              textAfterBadge={supportCaseStatusData(item.status).text}
              color={supportCaseStatusData(item.status).color}
            />
            <span className="of-link-list-summary__link__separator">|</span>
            <div className="of-link-list-summary__link__status__date">
              {t.RTJW(modifiedMsg)} {formatTimestampToDDMMYYYY(item.lastModified)}
            </div>
          </>
        ),
        path: `${generatePath(paths.MESSAGE_CENTER_SUPPORT_CASE, { supportCaseDisplayId: item.supportCaseDisplayId })}?mdmId=${item.accountMasterId}`,
      }));

    default:
      return [];
  }
};

export const LinkListSummary = ({
  linkListSummaryType,
  data,
  authenticatedUser,
  loading = false,
}: LinkListSummaryProps) => {
  const typeConfig = {
    invoices: {
      heading: t.INV3('Open invoices'),
      linkPath: paths.INVOICES,
      linkText: t.Y7C0(invoicesMsg),
      matchedCount: data?.length ?? 0,
      matchedCountLinkQuery: '?openInvoicesOnly=true',
      matchedCountText: '',
      icon: <CL.Icon icon="invoice" />,
      noItemsText: t.N4DE('No open invoices'),
      noItemsIcon: 'register',
      analyticsParentElement: 'NOE Invoices section',
    },
    redeemRequests: {
      heading: t.YDYY('Open redeem requests'),
      linkPath: paths.CUSTOMER_ORDER_REDEEM_REQUESTS,
      linkText: t.Z1M1(redeemRequestsMsg),
      matchedCount: data?.length ?? 0,
      matchedCountLinkQuery: '?openRedeemRequestsOnly=true',
      matchedCountText: '',
      icon: <CL.Icon icon="invoice" />,
      noItemsText: t.U9V3('No open redeem requests'),
      noItemsIcon: 'register',
      analyticsParentElement: 'NOE Redeem requests section',
    },
    orders: {
      heading: t.I9C0(ordersMsg),
      linkPath: paths.CUSTOMER_ORDERS,
      linkText: t.I9C0(ordersMsg),
      matchedCount:
        data?.filter((order: CustomerOrderHeader) => order.status === CustomerOrderStatus.PENDING_APPROVAL)?.length ??
        0,
      matchedCountLinkQuery: '?status=PENDING_APPROVAL',
      matchedCountText: t.ZXA6(pendingApprovalMsg),
      icon: <div className="of-link-list-summary__local-icon ea-icon--total-delivery"></div>,
      noItemsText: t.GBCS('No orders yet'),
      noItemsIcon: 'order',
      analyticsParentElement: 'NOE Orders section',
    },
    supportCases: {
      heading: t.NPCK(supportCasesAndMessagesMsg),
      linkPath: paths.MESSAGE_CENTER_SUPPORT_CASES,
      linkText: t.NPCK(supportCasesAndMessagesMsg),
      matchedCount:
        data?.filter(
          (supportCase: SupportCaseDataBundle) => supportCase.status === SupportCase.StatusEnum.WAITING_FOR_INPUT
        )?.length ?? 0,
      matchedCountLinkQuery: '?status=WAITING_FOR_INPUT',
      matchedCountText: t.WG63(waitingForActionMsg),
      icon: <CL.Icon icon="draft" />,
      noItemsText: t.WHMT('No support cases and messages yet'),
      noItemsIcon: 'invoice',
      analyticsParentElement: 'NOE Support cases and messages section',
    },
  };

  const config = typeConfig[linkListSummaryType];
  const linkItems = data ? mapDataToLinkItems(data.slice(0, 4), linkListSummaryType, authenticatedUser) : [];

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div className="of-link-list-summary" analytics-parent-element={config.analyticsParentElement}>
      <div className="of-link-list-summary__header">
        <div className="of-link-list-summary__header__heading">
          <h3>{config.heading}</h3>
          {config.matchedCount > 0 && (
            <div className="of-link-list-summary__header__heading__sub">
              <Link to={config.linkPath + config.matchedCountLinkQuery}>
                <span className="of-link-list-summary__header__heading__sub__count">
                  {t.BDBD('{} pcs', config.matchedCount?.toString() || '0')}
                </span>
              </Link>
              {config.matchedCountText}
            </div>
          )}
        </div>
        {linkItems.length > 0 && (
          <p>
            <Link
              className={`${dsClass.LINK} ${dsClass.LINK_STYLE_LINK_BUTTON} ${dsClass.LINK_BUTTON_COLOR_LIGHT} ${dsClass.LINK_BUTTON_SIZE_M}`}
              to={config.linkPath}
            >
              {config.linkText}{' '}
              <span className={dsClass.LINK_LAST_OBJECT}>
                <CL.Icon icon="arrow-right-alt" />
              </span>
            </Link>
          </p>
        )}
      </div>

      {loading ? (
        <Loading medium />
      ) : (
        <>
          {linkItems.map(item => {
            return (
              <CL.LinkBlock key={item.id} href={item.path} border="top" icon={config.icon}>
                <div className="of-link-list-summary__link">
                  <div className="of-link-list-summary__link__title">{item.title}</div>
                  {item.subtitle && <div className="of-link-list-summary__link__subtitle">{item.subtitle}</div>}
                  {item.status && <div className="of-link-list-summary__link__status">{item.status}</div>}
                </div>
              </CL.LinkBlock>
            );
          })}

          {!linkItems.length && (
            <div className="of-link-list-summary__empty">
              <div className={`of-link-list-summary__empty__icon ea-picto--${config.noItemsIcon}`}></div>
              {config.noItemsText}.
            </div>
          )}
        </>
      )}
    </div>
  );
};
